export interface Option<TLabel, TValue> {
  label: TLabel;
  value: TValue;
}

export const findOptionByValue = <TOption extends Option<TLabel, TValue>, TLabel, TValue>(
  options: ReadonlyArray<TOption>,
  value: TValue
): TOption | undefined => options.find((option) => option.value === value) ?? undefined;

/**
 * Sorts an array of options by moving items with errors to the end of the array.
 * This ensures that valid options appear first in dropdowns or selection lists.
 *
 * @param options - Array of option objects to sort
 * @param hasError - Function that determines if an option has an error
 * @returns A new sorted array with error-free options first
 */
export const rearrangeOptionsBasedOnError = <T extends object>(options: T[], hasError: (option: T) => boolean): T[] => {
  return options.sort((a, b) => {
    // If a has an error and b doesn't, a should come after b
    if (hasError(a) && !hasError(b)) {
      return 1;
    }
    // If b has an error and a doesn't, a should come before b
    if (!hasError(a) && hasError(b)) {
      return -1;
    }
    // If both have the same error status (both have errors or both don't), maintain original order
    return 0;
  });
};
